/**
 * DmlParseErrorPanel — compact escape surface shown when a DML renderer
 * (INSERT / UPDATE / DELETE / MERGE) can't parse its statement into the
 * structured editor. Replaces the bare "… could not be parsed" <p> dead-ends
 * with the same alert + "Edit as SQL" contract as ParseErrorFallback, so the
 * user always has a one-click path back to raw SQL.
 *
 * Copy comes from `vqeCopy.disabled.dmlParseError` (kind-aware) and the
 * "Edit as SQL" button label from `vqeCopy.banner.readOnly.cta` — never
 * hardcoded (see ux/vqeCopy.test.ts).
 */

import { Button } from '../../../../../components/ui/button';
import { vqeCopy } from '../../ux/vqeCopy';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

export type DmlParseErrorKind = 'insert' | 'update' | 'delete' | 'merge';

export interface DmlParseErrorPanelProps {
  readonly kind: DmlParseErrorKind;
  readonly onEditAsSql: () => void;
}

const KIND_TITLES: Record<DmlParseErrorKind, string> = {
  insert: 'INSERT — couldn’t parse',
  update: 'UPDATE — couldn’t parse',
  delete: 'DELETE — couldn’t parse',
  merge: 'MERGE — couldn’t parse',
};

export function DmlParseErrorPanel({ kind, onEditAsSql }: DmlParseErrorPanelProps) {
  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-dml-parse-error"
      data-kind={kind}
    >
      {/* Same one-card shape as the read-only / parse-error fallbacks. */}
      <div role="alert" className="rounded-lg border border-border bg-card p-4 shadow-sm">
        <h3 className="text-sm font-semibold text-foreground" data-testid="vqe-dml-parse-error-title">
          {KIND_TITLES[kind]}
        </h3>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="vqe-dml-parse-error-message">
          {vqeCopy.disabled.dmlParseError(kind)}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={onEditAsSql}
            data-testid="vqe-dml-parse-error-edit-as-sql"
          >
            {vqeCopy.banner.readOnly.cta}
          </Button>
        </div>
      </div>
    </div>
  );
}
